import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [wsToken, setWsToken] = useState(null);

  const fetchWsToken = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/ws-token`, { withCredentials: true });
      setWsToken(data.ws_token);
    } catch {
      setWsToken(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      // For portal members, also fetch organization data
      if (data.role === 'membre_externe') {
        try {
          const portalMe = await axios.get(`${API}/api/portal/me`, { withCredentials: true });
          data.organization = portalMe.data.organization;
          data.organization_id = portalMe.data.organization_id;
        } catch {}
      }
      setUser(data);
      await fetchWsToken();
    } catch (error) {
      setUser(false);
      setWsToken(null);
    } finally {
      setLoading(false);
    }
  }, [fetchWsToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.ws_token) setWsToken(data.ws_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const register = async (email, password, name, role = 'prospect', territoire = null, extra = {}) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/register`,
        { email, password, name, role, territoire, ...extra },
        { withCredentials: true }
      );
      if (data.ws_token) { setWsToken(data.ws_token); setUser(data); }
      return { success: true, verificationSent: !!data.verification_sent, message: data.message };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
      setWsToken(null);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, forgotPassword, resetPassword, checkAuth, wsToken, fetchWsToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
